import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { KeyRound, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/FormField'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ApiError } from '@/lib/api'
import { authApi } from '@/features/auth/api'

const errMsg = (err: unknown, fallback = 'Request failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function ForgotPasswordPage() {
  const [params] = useSearchParams()
  const isStaff = params.get('staff') === '1'
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setBusy(true)
    try {
      const r = await authApi.forgotPassword(email)
      setSuccess(r.message ?? 'If an account exists for that email, a reset link is on its way.')
    } catch (err) {
      setError(errMsg(err))
    } finally {
      setBusy(false)
    }
  }

  const backHref = isStaff ? '/admin-login' : '/login'
  const backLabel = isStaff ? '← Back to staff sign in' : '← Back to sign in'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-center text-2xl">
          {isStaff ? 'Staff password reset' : 'Forgot password'}
        </CardTitle>
        <p className="text-center text-sm text-muted-foreground">
          Enter your email and we&apos;ll send a reset link.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        {success ? (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        ) : null}
        <form onSubmit={submit} className="space-y-3">
          <FormField
            label="Email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
            Send reset link
          </Button>
        </form>
        <p className="text-center text-sm text-muted-foreground">
          <Link to={backHref} className="text-primary hover:underline">
            {backLabel}
          </Link>
        </p>
      </CardContent>
    </Card>
  )
}
